import { useMemo, useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import useSeo from '@/hooks/useSeo';
import { animeList } from '@/mocks/anime';
import { newsList } from '@/mocks/news';
import AnimeCard from '@/components/base/AnimeCard';
import EmptyState from '@/components/base/EmptyState';
import PageHero from '@/components/base/PageHero';

type Tab = 'anime' | 'news';

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q')?.trim() || '';
  const [input, setInput] = useState(query);
  const [tab, setTab] = useState<Tab>('anime');

  useEffect(() => {
    setInput(query);
  }, [query]);

  useSeo(
    query ? `“${query}”的搜索结果 - 妖精动漫` : '搜索 - 妖精动漫',
    query ? `妖精动漫搜索结果：与“${query}”相关的动漫作品与资讯。` : '在妖精动漫搜索你想看的动漫作品与相关资讯。',
    `搜索,${query},妖精动漫`,
  );

  const kw = query.toLowerCase();

  const animeResults = useMemo(() => {
    if (!kw) return [];
    return animeList.filter((a) =>
      a.title.toLowerCase().includes(kw) ||
      a.summary.toLowerCase().includes(kw) ||
      a.type.toLowerCase().includes(kw) ||
      a.region.toLowerCase().includes(kw) ||
      a.tags.some((t) => t.toLowerCase().includes(kw)),
    );
  }, [kw]);

  const newsResults = useMemo(() => {
    if (!kw) return [];
    return newsList.filter((n) =>
      n.title.toLowerCase().includes(kw) ||
      n.summary.toLowerCase().includes(kw) ||
      n.tags.some((t) => t.toLowerCase().includes(kw)),
    );
  }, [kw]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = input.trim();
    setSearchParams(v ? { q: v } : {});
  };

  const activeResults = tab === 'anime' ? animeResults : newsResults;

  return (
    <div className="w-full">
      <PageHero title="搜索结果" subtitle="输入关键词，查找动漫作品与相关资讯。" crumbs={[{ label: '首页', to: '/' }, { label: '搜索结果' }]} />

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <form onSubmit={submit} className="max-w-2xl">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="搜索动漫、类型、标签或资讯…"
                className="w-full h-12 pl-12 pr-4 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
              />
            </div>
            <button type="submit" className="h-12 px-6 rounded-md bg-accent-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer">
              搜索
            </button>
          </div>
        </form>

        {query ? (
          <>
            <p className="mt-4 text-sm text-foreground-500">
              关键词：<span className="text-accent-600 font-semibold">“{query}”</span>
              <span className="ml-2 text-xs text-foreground-400">（前端模拟搜索，演示效果）</span>
            </p>

            <div className="mt-5 inline-flex items-center gap-1 bg-background-100 rounded-full p-1">
              <button
                type="button"
                onClick={() => setTab('anime')}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${tab === 'anime' ? 'bg-primary-500 text-background-50 font-medium' : 'text-foreground-600 hover:text-foreground-900'}`}
              >
                动漫作品 ({animeResults.length})
              </button>
              <button
                type="button"
                onClick={() => setTab('news')}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${tab === 'news' ? 'bg-primary-500 text-background-50 font-medium' : 'text-foreground-600 hover:text-foreground-900'}`}
              >
                相关资讯 ({newsResults.length})
              </button>
            </div>

            <div className="mt-6">
              {activeResults.length === 0 ? (
                <EmptyState
                  icon="ri-search-eye-line"
                  title="未找到匹配内容"
                  description="换个关键词试试，或者缩短搜索词，也可以返回首页继续浏览推荐内容。"
                  actionText="返回首页"
                  actionTo="/"
                />
              ) : tab === 'anime' ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                  {animeResults.map((a) => (
                    <AnimeCard key={a.id} anime={a} />
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {newsResults.map((n) => (
                    <Link
                      key={n.id}
                      to={`/news/${n.id}`}
                      className="group flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm md:text-base font-semibold text-foreground-950 line-clamp-1 group-hover:text-primary-600 transition-colors">{n.title}</h3>
                        <p className="mt-1 text-xs md:text-sm text-foreground-500 line-clamp-1">{n.summary}</p>
                      </div>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{n.date}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="mt-8">
            <EmptyState
              icon="ri-search-line"
              title="输入关键词开始搜索"
              description="试试搜索作品名、动漫类型（如“热血”“治愈”）或资讯关键词。"
              actionText="去动漫分类逛逛"
              actionTo="/category"
            />
          </div>
        )}
      </div>
    </div>
  );
}